import React, { useState, useEffect } from 'react';
import { FaListAlt } from 'react-icons/fa';
import { reverseGeocode } from '../utils/reverseGeocode';
import { loadAoiPoints } from '../utils/aoiLoader';
import { useDownloadMap } from '../utils/useDownloadMap';

function formatScenarioId(id) {
  const match = id.match(/^Mw(\d+)_C(\d+)$/);
  if (!match) return id;
  const mag = (parseInt(match[1], 10) / 100).toFixed(1);
  const scenario = parseInt(match[2], 10);
  return `M ${mag} Scenario: ${scenario}`;
}

// Active-scenario summary + nearest-scenarios list + download button.
// Content-only, same pattern as EarthquakeControls — ControlPanel decides
// how it's presented (sidebar / drawer / bottom sheet).
export default function ScenarioList({ results, selected, onSelect, searchPin, maxCoastal, onAfterAction }) {
  const [placeLabel, setPlaceLabel] = useState('');
  const [aoiPoints, setAoiPoints] = useState(null);
  const { downloading, error: downloadError, download } = useDownloadMap(selected);

  const active = results.find(r => r.id === selected);

  useEffect(() => {
    if (!selected) { setAoiPoints(null); return; }
    let cancelled = false;
    loadAoiPoints(selected)
      .then(pts => { if (!cancelled) setAoiPoints(pts); })
      .catch(() => { if (!cancelled) setAoiPoints([]); });
    return () => { cancelled = true; };
  }, [selected]);

  useEffect(() => {
    if (!searchPin) { setPlaceLabel(''); return; }
    if (searchPin.place) { setPlaceLabel(searchPin.place); return; }
    let cancelled = false;
    setPlaceLabel('Locating…');
    reverseGeocode(searchPin.lat, searchPin.lng)
      .then(label => { if (!cancelled) setPlaceLabel(label); })
      .catch(() => { if (!cancelled) setPlaceLabel('—'); });
    return () => { cancelled = true; };
  }, [searchPin]);

  if (!results.length) return null;

  return (
    <div className="scl">
      <div className="eqc-section-label">
        <FaListAlt size={9} style={{ marginRight: 5, verticalAlign: 'middle' }} />
        Nearest Scenarios
      </div>

      {active && (() => {
        const peakWave = aoiPoints?.length
          ? Math.max(...aoiPoints.map(p => p.h))
          : null;
        return (
          <div className="active-summary">
            <div className="as-top">
              <span className="as-mag">M {active.magnitude.toFixed(1)}</span>
              <span className="as-id">Scenario {parseInt(active.id.slice(-3), 10)}</span>
            </div>
            <div className="as-source">{placeLabel || '—'}</div>
            <div className="as-stats">
              <div className="as-stat"><label>Distance</label><b>{active.distanceKm} km</b></div>
              <div className="as-stat"><label>Peak wave</label>
                <b style={{ color: '#8b0000' }}>{peakWave != null ? peakWave.toFixed(1) + ' m' : '—'}</b></div>
              <div className="as-stat"><label>Max coastal</label>
                <b style={{ color: '#8b0000' }}>{maxCoastal != null ? maxCoastal.toFixed(1) + ' m' : '—'}</b></div>
            </div>
          </div>
        );
      })()}

      <ul className="scenario-list">
        {results.map(r => (
          <li
            key={r.id}
            className={`scenario-item${r.id === selected ? ' active' : ''}`}
            onClick={() => { onSelect(r.id); onAfterAction?.(); }}
          >
            <span className="scenario-id">{formatScenarioId(r.id)}</span>
            <span className="scenario-dist">{r.distanceKm} km</span>
          </li>
        ))}
      </ul>

      {selected && (
        <>
          <button
            className="download-btn"
            onClick={download}
            disabled={downloading}
          >
            {downloading ? 'Downloading…' : '↓ Download Map'}
          </button>
          {downloadError && (
            <div className="download-error">{downloadError}</div>
          )}
        </>
      )}
    </div>
  );
}
